import { memo } from 'react';
import type { ReactNode } from 'react';
import Markdown from 'react-markdown';
import type { Components, Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Box } from '@mantine/core';
import { isBareUrl, remarkDisableIndentedCode } from './utils';
import { cn } from '@/utils';

type RemarkPlugin = NonNullable<Options['remarkPlugins']>[number];

const REMARK_PLUGINS: RemarkPlugin[] = [
    remarkDisableIndentedCode as RemarkPlugin,
    remarkGfm,
    remarkBreaks
];

const renderLink = (href: string | undefined, children: ReactNode) => (
    <a href={href} rel="noopener noreferrer" target="_blank">
        {children}
    </a>
);

const LINK_COMPONENTS: Components = {
    a: ({ href, children }) => renderLink(href, children)
};

const NESTED_REMARK_PLUGINS: RemarkPlugin[] = [remarkGfm];

const SECURE_DISALLOWED_ELEMENTS = ['img'];

const DISALLOWED_ELEMENTS = ['p', ...SECURE_DISALLOWED_ELEMENTS];

const COMPONENTS: Components = {
    a: ({ href, children }) => renderLink(href, children),
    code: ({ children, className }) =>
        !className && typeof children === 'string' && isBareUrl(children) ? (
            <Markdown
                unwrapDisallowed
                components={LINK_COMPONENTS}
                disallowedElements={DISALLOWED_ELEMENTS}
                remarkPlugins={NESTED_REMARK_PLUGINS}
            >
                {children}
            </Markdown>
        ) : (
            <code className={className}>{children}</code>
        )
};

const MARKDOWN_CLASSNAME = [
    'whitespace-normal',
    '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
    '[&_p]:my-1.5',
    '[&_ul]:my-1.5 [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:pl-5',
    '[&_blockquote]:my-1.5',
    '[&_code]:rounded-md [&_code]:bg-black/10 [&_code]:px-1.5 [&_code]:py-0.5',
    '[&_pre]:my-1.5 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-gray-900 [&_pre]:px-3 [&_pre]:py-2.5 [&_pre]:text-gray-50',
    '[&_pre_code]:bg-transparent [&_pre_code]:p-0'
];

interface MarkdownMessageProps {
    children: string;
    className?: string;
}

const MarkdownMessage = ({ children, className }: MarkdownMessageProps) => (
    <Box className={cn(...MARKDOWN_CLASSNAME, className)}>
        <Markdown
            components={COMPONENTS}
            disallowedElements={SECURE_DISALLOWED_ELEMENTS}
            remarkPlugins={REMARK_PLUGINS}
        >
            {children}
        </Markdown>
    </Box>
);

export default memo(MarkdownMessage);
